// src/App.js
import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import ErrorBoundary from './components/common/ErrorBoundary';
import { StyledEngineProvider, ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import es from 'date-fns/locale/es';
import Perfil from './pages/Perfil';
import theme from './theme';
import Login from './pages/Login';
import RecuperarContrasena from './pages/RecuperarContrasena';
import RestablecerContrasena from './pages/RestablecerContrasena';
import Layout from './components/common/Layout';
import Pacientes from './pages/Pacientes';
import Consultas from './pages/Consultas';
import Dashboard from './pages/Dashboard';
import Agenda from './pages/Agenda';
import Facturacion from './components/facturacion/Facturacion';
import AdminReporteHoras from './pages/AdminReporteHoras';

// 🚀 FIX: Importamos el nuevo componente refactorizado en lugar del viejo MonitorPresencia
import AdminFichajes from './pages/AdminFichajes';
import AdminAuditoria from './pages/AdminAuditoria';
import AdminConfiguracionFiscal from './pages/AdminConfiguracionFiscal';
import AdminVerifactu from './pages/AdminVerifactu';
import AdminPersonal from './pages/AdminPersonal';
import AdminTarifario from './pages/AdminTarifario';
import AdminAseguradoras from './pages/AdminAseguradoras';
import Legal from './pages/Legal';

// El nuevo lienzo clínico
import MedicalWorkspace from './components/MedicalWorkspace';

// Motores de Contexto
import { AuthProvider, useAuth } from './context/AuthContext';
import { ClinicaProvider } from './context/ClinicaContext';
import { ToastProvider } from './context/ToastContext';

/**
 * GESTOR DE RUTAS SUBORDINADO AL ESTADO DE CONTEXTO
 */
function AppRoutes() {
    const { user, loading, esSuperMedico, esAdmin, tieneAccesoGlobal, veHistoriaClinica } = useAuth();

    // 🛡️ ESCUDO ANTI-PARPADEO: Bloquea renderizados reactivos mientras /auth/me responde
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-900">
                <div className="flex flex-col items-center gap-4">
                    <div className="w-12 h-12 border-4 border-clinica-primary border-t-transparent rounded-full animate-spin"></div>
                    <span className="text-xs font-bold tracking-widest text-slate-400 uppercase">Verificando Credenciales...</span>
                </div>
            </div>
        );
    }

    return (
        <Routes>
            {/* 🔓 FLUJO PÚBLICO */}
            <Route
                path="/login"
                element={!user ? <Login /> : <Navigate to="/dashboard" replace />}
            />
            {/* Recuperar la contraseña por correo: también con sesión abierta (enlace del correo) */}
            <Route path="/recuperar-contrasena" element={<RecuperarContrasena />} />
            <Route path="/restablecer-contrasena" element={<RestablecerContrasena />} />

            {/* 🏥 NUEVA RUTA AISLADA: Estación Clínica (Focus Mode) - SIN LAYOUT */}
            <Route element={user ? <Outlet /> : <Navigate to="/login" replace />}>
                <Route path="/workspace/:consultaId" element={veHistoriaClinica ? <MedicalWorkspace /> : <Navigate to="/agenda" replace />} />
            </Route>

            {/* 🔒 FLUJO PRIVADO PROTEGIDO (Con menú lateral) */}
            <Route element={user ? <Layout /> : <Navigate to="/login" replace />}>
                <Route element={<ErrorBoundary><Outlet /></ErrorBoundary>}>

                    <Route path="/dashboard" element={<Dashboard />} />
                    <Route path="/agenda" element={<Agenda />} />
                    <Route path="/pacientes" element={<Pacientes />} />
                    <Route path="/pacientes/:id" element={<Pacientes />} />
                    <Route path="/consultas" element={<Consultas />} />
                    <Route path="/perfil" element={<Perfil user={user} />} />
                    <Route path="/legal" element={<Legal />} />

                    {/* 🚀 FIX: Ruta actualizada apuntando al nuevo componente */}
                    <Route path="/admin/fichajes" element={<AdminFichajes />} />

                    {/* 🛡️ RUTAS RESTRINGIDAS POR ROL (espejo del RBAC del backend) */}
                    {/* Facturación: recepción emite, supermedico/admin supervisan */}
                    <Route
                        path="/facturacion"
                        element={tieneAccesoGlobal ? <Facturacion /> : <Navigate to="/dashboard" replace />}
                    />
                    {/* VeriFactu: configurarlo y activarlo es cosa del administrador */}
                    <Route
                        path="/facturacion/verifactu"
                        element={esAdmin ? <AdminVerifactu /> : <Navigate to="/facturacion" replace />}
                    />
                    <Route
                        path="/admin/reportes"
                        element={(esSuperMedico || esAdmin) ? <AdminReporteHoras user={user} /> : <Navigate to="/dashboard" replace />}
                    />

                    {/* 🧾 Registro de auditoría legal: exclusivo del rol admin */}
                    {/* 🏢 Datos fiscales del emisor de facturas: exclusivo del rol admin */}
                    {/* 👥 Gestión de personal: exclusivo del rol admin */}
                    <Route
                        path="/admin/personal"
                        element={esAdmin ? <AdminPersonal /> : <Navigate to="/dashboard" replace />}
                    />
                    {/* Tarifario y bonos: exclusivo del rol admin */}
                    <Route
                        path="/admin/tarifario"
                        element={esAdmin ? <AdminTarifario /> : <Navigate to="/dashboard" replace />}
                    />
                    <Route path="/admin/clinica" element={<Navigate to="/admin/tarifario" replace />} />
                    {/* Aseguradoras y su baremo: exclusivo del rol admin */}
                    <Route
                        path="/admin/aseguradoras"
                        element={esAdmin ? <AdminAseguradoras /> : <Navigate to="/dashboard" replace />}
                    />
                    <Route
                        path="/admin/datos-fiscales"
                        element={esAdmin ? <AdminConfiguracionFiscal /> : <Navigate to="/dashboard" replace />}
                    />

                    <Route
                        path="/admin/auditoria"
                        element={esAdmin ? <AdminAuditoria /> : <Navigate to="/dashboard" replace />}
                    />
                </Route>
            </Route>

            {/* ⛔ COMODÍN CATCH-ALL */}
            <Route path="*" element={<Navigate to={user ? "/dashboard" : "/login"} replace />} />
        </Routes>
    );
}

/**
 * COMPONENTE RAÍZ DE CONFIGURACIÓN DE PROVEEDORES
 */
function App() {
    return (
        <StyledEngineProvider injectFirst>
            <ThemeProvider theme={theme}>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
                    <CssBaseline />
                    <ToastProvider>
                        <AuthProvider>
                            <ClinicaProvider>
                                <BrowserRouter>
                                    <AppRoutes />
                                </BrowserRouter>
                            </ClinicaProvider>
                        </AuthProvider>
                    </ToastProvider>
                </LocalizationProvider>
            </ThemeProvider>
        </StyledEngineProvider>
    );
}

export default App;